// Components
import { Feature } from '.generated/templates/Feature.Items.model';
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import { LayoutServicePageState, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';
import ConditionalRender from 'components/helpers/ConditionalWrapper/ConditionalRender';
import { DangerousHtml } from 'components/helpers/DangerousHtml/DangerousHtml';
import { addPxIfNeeded } from 'lib/string-utils';
import { useEffect, useRef, useState } from 'react';

export type CerosEmbedProps = Sitecore.Override.ComponentBase &
  Feature.Components.Ceros.Fields.CerosEmbed;

const CerosEmbed = ({ fields, rendering }: CerosEmbedProps): JSX.Element => {
  // Fail out if we don't have any fields
  if (!fields) {
    return <></>;
  }

  const originalSrc = fields.iframeUrlStatic.value;

  const sitecoreContext = useSitecoreContext();

  const pageState = sitecoreContext.sitecoreContext?.pageState ?? LayoutServicePageState.Normal;

  // Checking if we're in sitecore, even if we are not in experience editor
  // This supports EE and Preview
  const isInSitecore =
    (originalSrc?.indexOf('https://cm') ?? -1) >= 0 || pageState !== LayoutServicePageState.Normal;

  const autoHeight = !isInSitecore && fields.autoHeight?.value;

  // If we set auto-height use our API to proxy the iframe so it's in the same domain
  const frameSrc = autoHeight
    ? '/api/rewrite/iframe?' + new URLSearchParams({ url: originalSrc }).toString()
    : originalSrc;

  const [frameWidth, setFrameWidth] = useState(0);

  const [frameHeight, setFrameHeight] = useState(0);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const setDimensions = () => {
    setFrameWidth(containerRef.current?.clientWidth || 0);

    if (autoHeight) {
      setFrameHeight((iframeRef.current?.contentWindow?.document.body?.scrollHeight || 0) + 40);
    }
  };

  useEffect(() => {
    !fields?.iframeEmbedScriptDynamic.value && window.addEventListener('resize', setDimensions);
    return () => {
      window.removeEventListener('resize', setDimensions);
    };
  }, []);

  const width = fields.width?.value || `${frameWidth}px`;

  const height = autoHeight ? frameHeight : fields.height?.value || `${frameHeight}px`;

  const componentAnchorId = rendering?.uid;

  const cerosEmbedIframe = () => {
    return (
      <div
        data-component="authorable/general/cerosembed"
        className={classNames('anchorIdIndentifier')}
        id={componentAnchorId}
      >
        <ConditionalRender condition={!autoHeight}>
          <style
            dangerouslySetInnerHTML={{
              __html: `
                  #iframe-${componentAnchorId} {
                    height: ${addPxIfNeeded(fields.heightMobile?.value ?? fields.height?.value)};
                  }
                  @media (min-width: 960px) {
                    #iframe-${componentAnchorId} {
                      height: ${addPxIfNeeded(fields.height?.value)};
                    }
                  }
                  `,
            }}
          />
        </ConditionalRender>

        <div ref={containerRef}>
          {fields?.iframeEmbedScriptDynamic.value ? (
            <DangerousHtml html={fields?.iframeEmbedScriptDynamic.value} />
          ) : (
            <iframe
              id={`iframe-${componentAnchorId}`}
              ref={iframeRef}
              src={frameSrc}
              title={fields.title?.value}
              width={width}
              height={height}
              onLoad={setDimensions}
            />
          )}
        </div>
      </div>
    );
  };
  return (
    <>
      <div
        className={classNames(
          'flex',
          'flex-col',
          'flex-grow',
          'shrink-0',
          'justify-end',
          'md:justify-center',
          'overflow-hidden',
          'relative'
        )}
      >
        <div
          className={classNames('mx-auto', 'w-full', fields?.verticalPadding?.value, {
            'container-content-width': !fields?.isFullWidth?.value,
          })}
        >
          <div
            className={classNames('md:space-y-12', 'space-y-8', {
              'container-content-padding-x': !fields?.isFullWidth?.value,
            })}
          >
            {cerosEmbedIframe()}
          </div>
        </div>
      </div>
    </>
  );
};

export default CerosEmbed;
